import { useEffect, useState } from "react";
import {
  COACH_DRAFT_LABEL,
  COACH_MEMORY_CATEGORY_LABELS,
  coachMemorySourceLabel,
  type AiCoachDraft,
  type AiCoachDraftCaseKind,
  type AiCoachDraftMemoryUsed,
  type AiCoachDraftResult,
} from "@care-by-ella/contracts";
import {
  acceptCoachDraftMemory,
  coachDraftsAvailable,
  decideCoachDraft,
  generateCoachDraft,
} from "@/lib/coach-draft-api";
import { draftDate, usedMemorySummary } from "@/lib/coach-draft-composer";
import { UnauthorizedError } from "@/lib/clinician-api";
import { hiddenWhenReadOnly } from "./SupportView";

// TES-1237 — the coach's AI suggestion under the narrowed contract. QA only:
// renders nothing unless the server says the feature is on.
//
// The component never sends and never saves on its own. A draft's reply goes
// into the caller's composer through `onDraft`; the follow-up question and
// the next step can be appended to it; a memory suggestion is saved only on
// "Spara". The caller reports the send itself through
// `reportCoachDraftSent` below, so the audit says whether the coach edited
// the text first.
//
// TES-1351 (Emelie 2026-10-14, TES-1350 §1, §5):
//  - the reply arrives as one complete draft; a follow-up the model marked as
//    needed is already at its end (server side). An optional follow-up sits
//    separately with "Lägg till i svaret" and its own remove action, and
//    leaves the panel once used or removed.
//  - "Förkasta utkastet" hands the caller every text the AI put into the
//    composer; the caller removes those that are still unchanged
//    (removeDraftParts) and keeps whatever the coach wrote.
//  - the memory suggestion has its own Spara, Redigera and Avvisa.
// TES-1352 (§2) — "Underlag som använts · n uppgifter": the sanitised
// message and each Patientminne entry's own text, with "Rätta i
// Patientminnet" and "Använd inte denna uppgift" (a new draft without it).

const BTN =
  "rounded-md border border-surfaceMuted px-sm py-xs text-xs text-textPrimary hover:bg-surfaceMuted disabled:opacity-50";

function errorText(e: unknown): string {
  const code = e instanceof Error ? e.message : "";
  switch (code) {
    case "ai_assist_provider_unavailable":
      return "Modellen svarade inte. Försök igen.";
    case "ai_coach_draft_output_rejected":
      return "Förslaget stoppades av skyddsregeln och visas inte. Skriv svaret själv eller försök igen.";
    case "ai_coach_draft_message_not_current":
      return "Patienten har skrivit ett nyare meddelande. Ladda om och utgå från det.";
    case "coach_memory_forbidden_content":
      return "Patientminnet får inte innehålla namn, platser, kontaktuppgifter eller medicinska uppgifter. Skriv om posten utan dem.";
    default:
      return "Kunde inte ta fram ett förslag. Försök igen.";
  }
}

function stoppedText(
  r: Extract<AiCoachDraftResult, { status: "stopped" }>,
): string {
  if (r.acute) {
    return "Inget förslag. Meddelandet kan innehålla en akut signal: följ säkerhetsvägen och skicka det till läkare.";
  }
  if (r.reason === "unsanitisable") {
    return "Inget förslag. Meddelandet gick inte att avidentifiera säkert. Svara själv.";
  }
  return "Inget förslag. Meddelandet kan handla om något medicinskt. Svara själv och använd ”Skicka till läkare” om det behöver bedömas.";
}

// The send happened through the ordinary chat; this records that it began as
// the draft, and whether the coach changed it. Best-effort: the message is
// already sent, so a failed audit call must not look like a failed send.
export function reportCoachDraftSent(
  patientId: string,
  draft: { id: string; original: string },
  sentText: string,
): void {
  void decideCoachDraft(patientId, draft.id, {
    decision: "sent",
    edited: sentText.trim() !== draft.original.trim(),
  }).catch(() => undefined);
}

type ExtraState = "open" | "added" | "removed";

function UsedMemory({
  draft,
  busy,
  onCorrect,
  onExclude,
}: {
  draft: AiCoachDraft;
  busy: boolean;
  onCorrect?: (itemId: string) => void;
  onExclude: (itemId: string) => void;
}) {
  // `?? []` / `?? null`: the admin casts the answer, and an older API sent
  // neither field.
  const used: AiCoachDraftMemoryUsed[] = draft.memoryUsed ?? [];
  const message = draft.sanitisedMessage ?? null;
  return (
    <details className="text-textSecondary">
      <summary className="cursor-pointer select-none">
        {usedMemorySummary(used.length)}
      </summary>
      <div className="mt-xs flex flex-col gap-sm border-l border-surfaceMuted pl-sm">
        {message !== null ? (
          <div>
            <p>Patientens meddelande så som AI fick det</p>
            <p className="whitespace-pre-wrap text-textPrimary">{message}</p>
          </div>
        ) : null}
        {used.length === 0 ? (
          <p>
            {message !== null
              ? "Endast patientens aktuella meddelande användes."
              : "Inga uppgifter ur Patientminnet användes."}
          </p>
        ) : (
          <ul className="flex flex-col gap-sm">
            {used.map((m) => (
              <li key={m.id}>
                <p className="text-textPrimary">
                  <span className="font-medium">
                    {COACH_MEMORY_CATEGORY_LABELS[m.category] ?? m.category}:
                  </span>{" "}
                  ”{m.body}”
                  {m.agreement ? ` (överenskommelse: ${m.agreement})` : ""}
                </p>
                <p>
                  Källa: {coachMemorySourceLabel(m)} · Källdatum:{" "}
                  {draftDate(m.observedAt)} · Senast uppdaterad:{" "}
                  {draftDate(m.updatedAt)}
                </p>
                <div className="mt-xs flex flex-wrap gap-xs">
                  {onCorrect ? (
                    <button
                      type="button"
                      onClick={() => onCorrect(m.id)}
                      className={BTN}
                    >
                      Rätta i Patientminnet
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onExclude(m.id)}
                    disabled={busy}
                    className={BTN}
                  >
                    Använd inte denna uppgift
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {draft.memoryExcludedCount > 0 ? (
          <p>{draft.memoryExcludedCount} poster användes inte.</p>
        ) : null}
      </div>
    </details>
  );
}

function AiCoachDraftAssistPanel({
  patientId,
  caseKind,
  messageId,
  buttonLabel,
  onDraft,
  onAppend,
  onDiscard,
  onEscalate,
  onCorrectMemory,
  activeDraftId,
  onUnauthorized,
}: {
  patientId: string;
  caseKind: AiCoachDraftCaseKind;
  // The patient's current message, for a patient-message case.
  messageId?: string;
  buttonLabel: string;
  // Put the reply into the composer. `original` is what the model wrote.
  onDraft: (draft: { id: string; original: string }) => void;
  // Append to the end of the composer; never replaces its text.
  onAppend: (text: string) => void;
  // Remove these AI-inserted texts from the composer where unchanged.
  onDiscard: (inserted: string[]) => void;
  // Open the existing "Skicka till läkare" form on that message.
  onEscalate?: (messageId: string) => void;
  // Open the existing Patientminne correction on that entry. Without it the
  // "Rätta i Patientminnet" action is not offered.
  onCorrectMemory?: (itemId: string) => void;
  // The caller's current draft; extras show only while it is the same one.
  activeDraftId: string | null;
  onUnauthorized: () => void;
}) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState<"generate" | "memory" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stopped, setStopped] = useState<Extract<
    AiCoachDraftResult,
    { status: "stopped" }
  > | null>(null);
  const [draft, setDraft] = useState<AiCoachDraft | null>(null);
  // Every text this draft put into the composer, for "Förkasta utkastet".
  const [inserted, setInserted] = useState<string[]>([]);
  const [followUp, setFollowUp] = useState<ExtraState>("open");
  const [nextStep, setNextStep] = useState<ExtraState>("open");
  // "Använd inte denna uppgift", accumulated for this message.
  const [excluded, setExcluded] = useState<string[]>([]);
  const [memoryText, setMemoryText] = useState("");
  const [memoryEditing, setMemoryEditing] = useState(false);
  const [memoryDone, setMemoryDone] = useState<"saved" | "rejected" | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    void coachDraftsAvailable().then((on) => {
      if (!cancelled) setEnabled(on);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // A new message is a new case: forget the previous outcome.
  useEffect(() => {
    setStopped(null);
    setDraft(null);
    setInserted([]);
    setExcluded([]);
    setError(null);
  }, [messageId]);

  if (!enabled) return null;

  async function generate(excludeMemoryIds: string[] = excluded) {
    setBusy("generate");
    setError(null);
    setStopped(null);
    try {
      const res = await generateCoachDraft(patientId, {
        caseKind,
        ...(messageId ? { messageId } : {}),
        ...(excludeMemoryIds.length > 0 ? { excludeMemoryIds } : {}),
      });
      // A new draft replaces the previous one's text, never the coach's.
      if (draft) {
        void decideCoachDraft(patientId, draft.id, {
          decision: "discarded",
        }).catch(() => undefined);
        onDiscard(inserted);
      }
      if (res.status === "stopped") {
        setDraft(null);
        setInserted([]);
        setStopped(res);
        return;
      }
      setDraft(res.draft);
      setInserted([res.draft.reply]);
      setFollowUp("open");
      setNextStep("open");
      setMemoryText(res.draft.memorySuggestion?.body ?? "");
      setMemoryEditing(false);
      setMemoryDone(null);
      onDraft({ id: res.draft.id, original: res.draft.reply });
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  function exclude(itemId: string) {
    const next = excluded.includes(itemId) ? excluded : [...excluded, itemId];
    setExcluded(next);
    void generate(next);
  }

  function append(text: string, mark: (s: ExtraState) => void) {
    onAppend(text);
    setInserted((xs) => [...xs, text]);
    mark("added");
  }

  async function saveMemory() {
    if (!draft?.memorySuggestion) return;
    setBusy("memory");
    setError(null);
    try {
      await acceptCoachDraftMemory(patientId, draft.id, {
        category: draft.memorySuggestion.category,
        body: memoryText.trim(),
      });
      setMemoryDone("saved");
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  function rejectMemory() {
    if (!draft) return;
    setMemoryDone("rejected");
    void decideCoachDraft(patientId, draft.id, {
      decision: "memory_rejected",
    }).catch(() => undefined);
  }

  function discard() {
    if (!draft) return;
    void decideCoachDraft(patientId, draft.id, {
      decision: "discarded",
    }).catch(() => undefined);
    setDraft(null);
    onDiscard(inserted);
    setInserted([]);
  }

  const showDraft = draft && activeDraftId === draft.id;

  return (
    <div className="mt-sm flex flex-col gap-xs text-xs">
      {!showDraft ? (
        <div>
          <button
            type="button"
            onClick={() => void generate()}
            disabled={busy !== null}
            className={BTN}
          >
            {busy === "generate" ? "Tar fram förslag…" : buttonLabel}
          </button>
        </div>
      ) : null}

      {stopped ? (
        <div className="rounded-md border border-warning bg-surface p-sm text-textPrimary">
          <p>{stoppedText(stopped)}</p>
          {stopped.escalation && onEscalate ? (
            <button
              type="button"
              onClick={() => onEscalate(stopped.escalation!.messageId)}
              className={`mt-xs ${BTN}`}
            >
              Skicka till läkare
            </button>
          ) : null}
        </div>
      ) : null}

      {showDraft ? (
        <div className="flex flex-col gap-xs rounded-md border border-surfaceMuted bg-surface p-sm">
          <p className="font-medium text-textPrimary">{COACH_DRAFT_LABEL}</p>
          <UsedMemory
            draft={draft}
            busy={busy !== null}
            onCorrect={onCorrectMemory}
            onExclude={exclude}
          />
          {busy === "generate" ? (
            <p className="text-textSecondary">Tar fram förslag…</p>
          ) : null}
          {draft.followUpQuestion && followUp === "open" ? (
            <div>
              <p className="text-textSecondary">Följdfråga</p>
              <p className="text-textPrimary">{draft.followUpQuestion}</p>
              <div className="mt-xs flex flex-wrap gap-xs">
                <button
                  type="button"
                  onClick={() => append(draft.followUpQuestion!, setFollowUp)}
                  className={BTN}
                >
                  Lägg till i svaret
                </button>
                <button
                  type="button"
                  onClick={() => setFollowUp("removed")}
                  className={BTN}
                >
                  Ta bort följdfrågan
                </button>
              </div>
            </div>
          ) : null}
          {draft.nextStep && nextStep === "open" ? (
            <div>
              <p className="text-textSecondary">Nästa steg</p>
              <p className="text-textPrimary">{draft.nextStep}</p>
              <button
                type="button"
                onClick={() => append(draft.nextStep!, setNextStep)}
                className={`mt-xs ${BTN}`}
              >
                Lägg till i svaret
              </button>
            </div>
          ) : null}
          {draft.memorySuggestion && memoryDone === null ? (
            <div>
              <p className="text-textSecondary">
                Förslag till Patientminnet ·{" "}
                {COACH_MEMORY_CATEGORY_LABELS[draft.memorySuggestion.category]}
              </p>
              {memoryEditing ? (
                <input
                  value={memoryText}
                  onChange={(e) => setMemoryText(e.target.value)}
                  aria-label="Förslag till Patientminnet"
                  className="mt-xs w-full rounded-md border border-surfaceMuted bg-surface p-xs text-xs"
                />
              ) : (
                <p className="text-textPrimary">{memoryText}</p>
              )}
              <div className="mt-xs flex gap-xs">
                <button
                  type="button"
                  onClick={() => void saveMemory()}
                  disabled={busy !== null || !memoryText.trim()}
                  className={BTN}
                >
                  {busy === "memory" ? "Sparar…" : "Spara"}
                </button>
                {memoryEditing ? null : (
                  <button
                    type="button"
                    onClick={() => setMemoryEditing(true)}
                    disabled={busy !== null}
                    className={BTN}
                  >
                    Redigera
                  </button>
                )}
                <button
                  type="button"
                  onClick={rejectMemory}
                  disabled={busy !== null}
                  className={BTN}
                >
                  Avvisa
                </button>
              </div>
            </div>
          ) : null}
          {memoryDone === "saved" ? (
            <p className="text-textSecondary">Sparat i Patientminnet.</p>
          ) : null}
          <div>
            <button
              type="button"
              onClick={discard}
              disabled={busy !== null}
              className={BTN}
            >
              Förkasta utkastet
            </button>
          </div>
        </div>
      ) : null}

      {error ? <p className="text-danger">{error}</p> : null}
    </div>
  );
}

// Hidden in the admin's read-only support view: it writes (a model call, a
// decision, a memory row).
export const AiCoachDraftAssist = hiddenWhenReadOnly(AiCoachDraftAssistPanel);
